import { defineAsyncComponent, watch } from 'vue'
import type { PopupInterface } from '~~/server/types/popup'
import { useGlobalDialog } from '~/composables/useGlobalDialog'

const PopupDialogComponent = defineAsyncComponent(
  () => import('~/components/dialogs/PopupDialogComponent.vue'),
)

const waitUntil = async (deadline: number) => {
  while (Date.now() < deadline) {
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, Math.min(deadline - Date.now(), 2_147_483_647))
    })
  }
}

const isWithinDates = (popup: PopupInterface) => {
  const now = Date.now()
  return (!popup.starts_at || Date.parse(popup.starts_at) <= now)
    && (!popup.ends_at || Date.parse(popup.ends_at) >= now)
}

export function usePopups() {
  const { isOpen, openDialog } = useGlobalDialog()
  const shownIds = useCookie<number[]>('shown_popup_ids', {
    default: () => [],
    maxAge: 60 * 60 * 24 * 400,
    path: '/',
    sameSite: 'lax',
  })

  const wasShown = (id: number) => Array.isArray(shownIds.value) && shownIds.value.includes(id)
  const markShown = (id: number) => {
    if (!wasShown(id)) shownIds.value = [...(Array.isArray(shownIds.value) ? shownIds.value : []), id]
  }

  const waitForDialog = () => new Promise<void>((resolve) => {
    if (!isOpen.value) {
      resolve()
      return
    }

    const stop = watch(isOpen, (open) => {
      if (!open) {
        stop()
        resolve()
      }
    }, { flush: 'sync' })
  })

  const start = async (popups: PopupInterface[], enteredAt: number) => {
    const scheduled = [...popups].sort(
      (a, b) => a.delay_seconds - b.delay_seconds || a.id - b.id,
    )

    for (const popup of scheduled) {
      if (wasShown(popup.id)) continue

      const deadline = enteredAt + Math.max(0, popup.delay_seconds) * 1000
      await waitUntil(deadline)

      while (isOpen.value) await waitForDialog()
      if (wasShown(popup.id) || !isWithinDates(popup)) continue

      await openDialog(PopupDialogComponent, {
        title: popup.name,
        width: 'min(760px, calc(100vw - 32px))',
        props: { popup },
      })
      markShown(popup.id)
    }
  }

  return { start }
}
